/**
 * AI Chat features — mirrors the backend AIChatFeature enum.
 * Add new values here as new features are onboarded.
 */
export enum AIChatFeature {
    COPY_GENERATOR = 'copy-generator',
}

/**
 * Guardrail types — mirrors the backend GuardrailType union.
 * Input/output are classifier guardrails; *-system-prompt-* types set channel-specific AI instructions.
 */
export const GUARDRAIL_TYPES = [
    'input',
    'output',
    'email-system-prompt-header',
    'email-system-prompt-footer',
    'sms-system-prompt-header',
    'sms-system-prompt-footer',
] as const;

export type GuardrailType = typeof GUARDRAIL_TYPES[number];

/**
 * Paths
 */
export enum URLPaths {
    CONTACTS = '/contacts',
    CONTENT = '/content',
    CONTENT_ARCHIVED = '/content/archived',
    CONTENT_IMPORT_TEMPLATE = '/content/import-template',
    CONTENT_FOLDER = '/content/folder',
    CONTENT_CREATE_DIRECT_MAIL = '/content/create/direct-mail',
    CONTENT_CREATE_EMAIL = '/content/create/email',
    CONTENT_CREATE_PAGE = '/content/create/page',
    CONTENT_CREATE_SMS = '/content/create/sms',
    CONTENT_CREATE_VOICE_CALL = '/content/create/voice',
    CONTENT_CREATE_WHATSAPP = '/content/create/whatsapp',
    CONTENT_VIEW_SCREEN = '/content/view',
    CONTENT_VIEW_DIRECT_MAIL = '/content/view/direct-mail',
    CONTENT_VIEW_EMAIL = '/content/view/email',
    CONTENT_VIEW_SMS = '/content/view/sms',
    CONTENT_VIEW_VOICE_CALL = '/content/view/voice',
    CONTENT_VIEW_WHATSAPP = '/content/view/whatsapp',
    CURATIONS = '/curations',
    CURATIONS_ARCHIVED = '/curations/archived',
    CAMPAIGNS = '/campaigns',
    CURATIONS_CONDITIONAL_SPLIT = '/curations/email/conditional-split',
    CURATIONS_CONDITIONAL_SPLIT_SEGMENT = '/curations/email/conditional-split/audience',
    CURATIONS_CONDITIONAL_SPLIT_1_CONTENT = '/curations/email/conditional-split/1/content',
    CURATIONS_CONDITIONAL_SPLIT_SCHEDULE = '/curations/email/conditional-split/schedule',
    CURATIONS_CONDITIONAL_SPLIT_DELAY = '/curations/email/conditional-split/delay',
    CURATIONS_CONDITIONAL_SPLIT_SEGMENTS = '/curations/email/conditional-split/segments',
    CURATIONS_CONDITIONAL_SPLIT_SEGMENTS_MEETS_ACTION = '/curations/email/conditional-split/segments-meets/action',
    CURATIONS_CONDITIONAL_SPLIT_SEGMENTS_MEETS_CONTENT = '/curations/email/conditional-split/segments-meets/content',
    CURATIONS_CONDITIONAL_SPLIT_SEGMENTS_MEETS_NOT_ACTION = '/curations/email/conditional-split/segments-meets-not/action',
    CURATIONS_CONDITIONAL_SPLIT_SEGMENTS_MEETS_NOT_CONTENT = '/curations/email/conditional-split/segments-meets-not/content',
    CURATIONS_CONDITIONAL_SPLIT_PUBLISH = '/curations/email/conditional-split/publish',
    CURATIONS_CUSTOM = '/curations/custom',
    CURATIONS_DETAILS = '/curations/details',
    CURATIONS_EMAIL_SERIES = '/curations/email/email-series',
    CURATIONS_LANDING = '/curations/create',
    CURATIONS_SINGLE_SEND = '/curations/email/single-send',
    HOME_PAGE = '/',
    INSIGHTS = '/insights',
    SEGMENTS = '/audiences',
    SEGMENTS_ARCHIVED = '/audiences/archived',
    SEGMENTS_FOLDER = '/audiences/folder',
    SEGMENT_CREATE = '/audiences/create',
    SEGMENTS_CONTACTS = '/audiences/contacts',
    UNSUBSCRIBE = '/unsubscribe',
    VIEW_EMAIL = '/view-email',
    VALUE = '/value',
    VALUE_CREATE = '/value/create',
    VALUE_SEND_SPECIALTY = '/value/create/send-specialty',
    VALUE_CONVERSION_EVENT = '/value/create/conversion-event',
    VALUE_CONVERSION_METRIC = '/value/create/conversion-metric',
    VALUE_CONVERSION_VALUE = '/value/create/conversion-value',
    VALUE_DETAILS = '/value/details',
    INNOVACCER_LOGIN = '/innovaccer/login',
    SETTINGS_USERS = '/settings/users',
    SETTINGS_ROLES = '/settings/roles',
    SETTINGS_BRAND = '/settings/brand',
    SETTINGS_BRAND_EDIT = '/settings/brand/edit',
    SETTINGS_CUSTOMER_SUPPORT = '/settings/customer-support',
    SETTINGS_AI_GUARDRAILS = '/settings/ai-guardrails',
}

export enum CuredApiPaths {
    AGE_PREDICTIONS = 'predictions/age',
    APPLCIATIONS_ACCESS_LIST = 'content/applications-access',
    APP_SENDS = 'app/sends',
    APP_SENDS_VALUE_ASSUMPTION = 'app/sends/for-value-assumption',
    APP_CLICK_APPOINTMENT_VALUE = 'app/click-appointment-value',
    ASSESSMENTS = 'assessments',
    ASSESSMENT_CONSENT = 'assessments/consent',
    AUDIENCE_COUNT = 'audiences/count',
    CAMPAIGNS = 'campaigns',
    CAMPAIGNS_PAGINATED = 'campaigns/paginated',
    CAMPAIGNS_AUDIT = 'campaigns/audit',
    CAMPAIGNS_DRAFT = 'campaigns/draft',
    CAMPAIGNS_AB_TEST_INSIGHTS = 'campaigns/abTest/insights',
    CAMPAIGNS_AB_TEST_DECLARE_WINNER = 'campaigns/abTest/declare-winner',
    CAMPAIGNS_AB_TEST_SCHEDULE_WINNER = 'campaigns/abTest/schedule-winner',
    CAMPAIGNS_EXIT_CRITERIA = 'campaigns/exit-criteria',
    CAMPAIGNS_EXIT_STATS = 'campaigns/exit-stats',
    CAMPAIGNS_ARCHIVE = 'campaigns/archive',
    CAMPAIGNS_UNARCHIVE = 'campaigns/unarchive',
    CAMPAIGNS_ARCHIVED_LIST = 'campaigns/archived',
    CAMPAIGNS_ARCHIVED_PAGINATED = 'campaigns/archived/paginated',
    CAMPAIGN_TEMPLATES = 'campaign-templates',
    CLICK_APPOINTMENT_VALUE_ASSUMPTIONS = 'click-appointment-value-assumptions',
    COMMUNICATION_TYPES_LIST = 'content/communication-types',
    CONTACT_COLUMNS = 'contacts/columns',
    CUSTOMER_SUPPORT_CONTACT_COLUMNS = 'customer-support/contacts/columns',
    CONTACT_EVENT_COLUMNS = 'contact-events/columns',
    CUSTOMER_SUPPORT_CONTACT_EVENT_COLUMNS = 'customer-support/contact-events/columns',
    CONTENT_EDITOR_CONFIG_EMAIL = 'content-editor/config/email',
    CONTENT_EDITOR_CONFIG_PAGE = 'content-editor/config/page',
    CONTENT_PERSONALIZATION_DEFAULTS = '/content-editor/personalization-default',
    CONTACT_FIELDS = 'contacts/fields',
    CONTACTS = 'contacts',
    CONTACTS_PAGINATED = 'contacts/paginated',
    CONTENT = 'content',
    CONTENT_PAGINATED = 'content/paginated',
    CONTENT_ARCHIVE = 'content/archive',
    CONTENT_UNARCHIVE = 'content/unarchive',
    CONTENT_UPDATE_STATUS = 'content/update-status',
    CONTENT_ARCHIVED_LIST = '/content/archived',
    CONTENT_ARCHIVED_PAGINATED = 'content/archived/paginated',
    CONTENT_FOLDER_LINEAGE = 'content/lineage',
    CONTENT_FOLDERS = 'content/folder',
    CONTENT_MOVE = 'content/move',
    CONTENT_RENAME = 'content/rename',
    COPY_GENERATOR_GENERATE = 'copy-generator/generate',
    COPY_GENERATOR_HISTORY = 'copy-generator/history',
    CURATIONS = 'curations',
    CSV_TO_CLIENT_DB = 'csv-to-client-db',
    DIRECT_MAIL = 'direct-mail',
    DIRECT_MAIL_PDF_PREVIEW = 'direct-mail/pdf-preview',
    DIRECT_MAIL_SAVE_PDF = 'direct-mail/savePdf',
    FEATURE_SETTINGS = 'feature-settings',
    BRAND_PROFILE = 'brand-profile',
    ACCESS_CENTRE_ME = 'access-centre/me',
    ACCESS_CENTRE_PERMISSIONS = 'access-centre/permissions',
    ACCESS_CENTRE_ROLES = 'access-centre/roles',
    ACCESS_CENTRE_USERS = 'access-centre/users',
    ACCESS_CENTRE_USERS_BULK_ASSIGN_ROLE = 'access-centre/users/bulk-assign-role',
    LD_HASH = 'feature-flag/hash',
    LANDING_PAGES = 'landing-pages',
    SEGMENT_FILTER_ENGAGEMENT_OPERATORS = 'segments/filters/engagement/operators',
    SEGMENT_FILTERS = 'segments/filters',
    SEGMENTS_PREVIEW_COUNT = 'segments/preview/count',
    SEGMENT_FOLDER_LINEAGE = 'segments/lineage',
    SEGMENT_FOLDERS = 'segments/folder',
    SEGMENTS_PAGINATED = 'segments/paginated',
    SEGMENTS = 'segments',
    SEGMENTS_ARCHIVE = 'segments/archive',
    SEGMENTS_UNARCHIVE = 'segments/unarchive',
    SEGMENTS_ARCHIVED = '/segments/archived',
    SEGMENTS_ARCHIVED_LIST = 'segments/archived',
    SEGMENTS_ARCHIVED_PAGINATED = 'segments/archived/paginated',
    SEGMENTS_FOLDER_RENAME = 'segments/rename',
    SEGMENT_MOVE = 'segments/move',
    SENDS = 'sends',
    SENDER_PROFILES = 'sender-profiles',
    SENDER_PROFILES_VOICE = 'sender-profiles/voice',
    SAVED_ROWS = 'saved-rows',
    SMS = 'sms',
    WHATSAPP_TEMPLATES = 'whatsapp-templates',
    STATE_PREDICTIONS = 'predictions/state',
    TASKS = 'tasks',
    TEMPLATES = 'templates',
    TEMPLATES_MIGRATE_PARSE = 'templates/migrate/parse',
    TEMPLATES_MIGRATE_EXECUTE = 'templates/migrate/execute',
    TEMPLATES_MIGRATE_MAPPINGS = 'templates/migrate/mappings',
    INCONNECT_TEMPLATES = 'inconnect/templates',
    INCONNECT_IMPORT = 'inconnect/import',
    TEST_EMAILS = 'test-emails',
    TEST_SMS = 'test-sms',
    TOKENS_UPLOAD = 'tokens/upload',
    VOICE_CONTENT = 'voice-templates',
    IMPORTS = 'imports',
    CLIENT_UAM_CONFIG = 'clients/innovaccer-config',
    INNOVACCER_LOGIN = 'app/innovaccer/login',
    INNOVACCER_USER = 'app/innovaccer/user',
    REPORT_CONTACTS_COUNT = '/app/report/contact/count',
    REPORT_CONTACTS_DOMAINS = '/app/report/contact/domains',
    REPORT_CONTACTS_LOCATIONS = '/app/report/contact/locations',
    REPORT_CONTACTS_SUBSCRIPTION_STATUS = '/app/report/contact/subscription-status',
    REPORT_CONTACTS_SUBSCRIBES = '/app/report/contact/subscribes',
    REPORT_CONTACTS_UNSUBSCRIBES = '/app/report/contact/unsubscribes',
    REPORT_CONTACTS_GENDER = '/app/report/contact/gender',
    REPORT_CONTACTS_AGES = '/app/report/contact/ages',
    REPORT_CONTACTS_DOWNLOAD = '/app/report/contact/download',
    REPORT_CURATION_STATS = '/app/report/curation/stats',
    REPORT_CURATION_MESSAGES = '/app/report/curation/messages-over-time',
    REPORT_CURATION_DEVICE_OPENS = '/app/report/curation/device-opens',
    REPORT_CURATION_DEVICE_CLICKS = '/app/report/curation/device-clicks',
    REPORT_CURATION_TOP_CLICKS = '/app/report/curation/top-clicks',
    REPORT_CURATION_UNIQUE_CLICKS = '/app/report/curation/unique-clicks',
    REPORT_CURATION_UNIQUE_OPENS = '/app/report/curation/unique-opens',
    REPORT_CURATION_ENGAGEMENT = '/app/report/curation/engagement',
    REPORT_CURATION_ENGAGEMENT_FUNNEL = '/app/report/curation/engagement-funnel',
    REPORT_CURATION_FILTER_DATA = '/app/report/curation/active-ids',
    REPORT_CURATION_DOWNLOAD = '/app/report/curation/download',
    REPORT_OVERVIEW_STATS = '/app/report/overview/stats',
    REPORT_UNIQUE_RATES = '/app/report/overview/unique-rates',
    REPORT_OVERVIEW_TOP_OPENS = '/app/report/overview/top-opens',
    REPORT_OVERVIEW_TOP_CLICKS = '/app/report/overview/top-clicks',
    REPORT_OVERVIEW_DOWNLOAD = '/app/report/overview/download',
    REPORT_VALUE_FILTERS = '/app/report/value/filters',
    REPORT_VALUE_STATS = '/app/report/value/stats',
    REPORT_VALUE_SUMMARY = '/app/report/value/summary',
    REPORT_VALUE_UNIQUE_CLICKS = '/app/report/value/unique-clicks',
    REPORT_VALUE_DOWNLOAD = '/app/report/value/download',
    REPORT_SMS_STATS = '/app/report/sms/stats',
    REPORT_SMS_UNSUBSCRIBES = '/app/report/sms/unsubscribes',
    REPORT_SMS_SUBSCRIBES = '/app/report/sms/subscribes',
    REPORT_SMS_MESSAGES_OVER_TIME = '/app/report/sms/messages-over-time',
    REPORT_SMS_METRICS = '/app/report/sms/metrics',
    REPORT_SMS_RESPONSE_BY_KEYWORD = 'app/report/sms/response-by-keyword',
    REPORT_SMS_FILTER_DATA = '/app/report/sms/active-ids',
    REPORT_SMS_DOWNLOAD = '/app/report/sms/download',
    REPORT_VOICE_CALL_ACTIONS_TAKEN = '/app/report/voice/actions-taken',
    REPORT_VOICE_CALL_ATTEMPTED_ANSWERED_OVER_TIME = '/app/report/voice/call-attempted-vs-answered',
    REPORT_VOICE_CALL_DURATION_DENSITY_OVER_TIME = '/app/report/voice/call-duration-density',
    REPORT_VOICE_CALL_VOLUME_OVER_TIME = '/app/report/voice/call-metrics-over-time',
    REPORT_VOICE_DOWNLOAD = '/app/report/voice/download',
    REPORT_VOICE_FILTER_DATA = '/app/report/voice/active-ids',
    REPORT_VOICE_METRICS = '/app/report/voice/metrics',
    REPORT_VOICE_OPT_OUT_RATE_OVER_TIME = '/app/report/voice/opt-out-rate-over-time',
    REPORT_VOICE_STATS = '/app/report/voice/stats',
    ROW_DISPLAY_CONDITIONS = '/dynamic-content/conditions',
    MANAGE_DISPLAY_CONDITIONS = '/dynamic-content/conditions/manage',
    DYNAMIC_CONTENT_CATEGORIES = '/dynamic-content/conditions/categories',
    AI_GUARDRAILS = 'ai-guardrails',
    VIEW_EMAIL = 'view-email',
}

/**
 * Download Query Keys
 */
export enum DownloadQueryKeys {
    CURATIONS_DASHBOARD = 'curationsDashboardDownload',
    CONTACTS_DASHBOARD = 'contactsDashboardDownload',
    EXECUTIVE_OVERVIEW_DASHBOARD = 'executiveOverviewDashboardDownload',
    SMS_DASHBOARD = 'smsDashboardDownload',
    VALUE_OVERVIEW_DASHBOARD = 'valueOverviewDashboardDownload',
    VOICE_DASHBOARD = 'voiceDashboardDownload',
}

/**
 * Dashboard Names
 */
export enum DashboardNames {
    EXECUTIVE_OVERVIEW = 'Executive Overview Dashboard',
    VALUE_OVERVIEW = 'Value Overview Dashboard',
    CURATIONS = 'Curations Dashboard',
    TEXT_MESSAGE = 'Text Message Dashboard',
    CONTACTS = 'Contacts Dashboard',
    VOICE = 'Voice Dashboard',
}

export enum CampaignStatus {
    DRAFT = 'DRAFT',
    SCHEDULED = 'SCHEDULED',
    SENDING = 'SENDING',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    CANCELED = 'CANCELED',
    STOPPED = 'STOPPED',
    FINISHING = 'FINISHING',
    DELETED = 'DELETED',
    PAUSED = 'PAUSED',
    RESUMED = 'RESUMED',
    // AB Test winner phase statuses
    SENDING_AB_TEST = 'SENDING_AB_TEST',
    PENDING_WINNER_SELECTION = 'PENDING_WINNER_SELECTION',
    WINNER_DECLARED = 'WINNER_DECLARED',
    WINNER_SCHEDULED = 'WINNER_SCHEDULED',
    SENDING_WINNER = 'SENDING_WINNER',
    WINNER_SENT = 'WINNER_SENT',
    WINNER_FAILED = 'WINNER_FAILED',
    WINNER_STOPPED = 'WINNER_STOPPED',
}

export enum CampaignType {
    SINGLE_SEND = 'single send',
    TIME_DELAY = 'time delay',
    CONDITIONAL_SPLIT = 'conditional split',
    FLEXIBLE = 'flexible',
}

export enum SendJobStatus {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN PROGRESS',
    DELIVERED = 'DELIVERED',
    FAILED = 'FAILED',
    CANCELED = 'CANCELED',
}

export enum AudienceStatus {
    PENDING = 'pending',
    ACTIVE = 'active',
    COMPLETE = 'complete',
    FAILED = 'failed',
    CANCELED = 'canceled',
    PAUSED = 'paused',
}

/**
 *  Flow
 */
export enum CurationNavState {
    PREVIEW = 'PREVIEW',
    OPEN = 'OPEN',
    IN_PROGRESS = 'IN_PROGRESS',
    IN_PROGRESS_CURRENT = 'IN_PROGRESS_CURRENT',
    COMPLETED = 'COMPLETED',
}

export enum CurationSegments {
    DID_CLICK = 'did click',
    DID_OPEN = 'did open',
}

export enum CurationSegmentsAction {
    SEND_EMAIL = 'send email',
    EXIT_CURATION = 'exit curation',
}

export enum CurationStep {
    SEGMENT = 'audience',
    CURATION = 'curation',
    EMAIL = 'email',
    SEGMENTS = 'conditional split',
    FIRST_EMAIL = 'first email',
    PREVIEW = 'preview',
    PUBLISH = 'publish',
    TEST = 'test',
    TIME_DELAY = 'time delay',
    SECOND_EMAIL = 'second email',
    SCHEDULE = 'schedule',
    SMS = 'sms',
}

/**
 * Contact
 */
export enum FilterCategory {
    CONTACT_DETAILS = 'contact details',
    CONTACT_ACTIVITY = 'contact activity',
    CONTACT_EVENTS = 'contact events',
    CONTACT_OBJECTS = 'contact objects',
}

export enum RecordType {
    ALL_CONTACTS = 'all contacts',
}

/**
 * Miscellaneous
 */
export enum ApiMethods {
    GET = 'get',
    DELETE = 'delete',
    HEAD = 'head',
    OPTIONS = 'options',
    POST = 'post',
    PUT = 'put',
    PATCH = 'patch',
    LINK = 'link',
    UNLINK = 'unlink',
}

export enum BeeConfigType {
    EMAIL = 'email',
    PAGE = 'page',
}

export enum MutationStatus {
    IDLE = 'idle',
    LOADING = 'loading',
    ERROR = 'error',
    SUCCESS = 'success',
}

export enum SortDirection {
    ASCENDING = 'asc',
    DESCENDING = 'desc',
}

export enum SQLType {
    BOOLEAN = 'boolean',
    CHARACTER_VARYING = 'character varying',
    DATE = 'date',
    DOUBLE_PRECISION = 'double precision',
    INTEGER = 'integer',
    TIMESTAMP_WITH_TIME_ZONE = 'timestamp with time zone',
    CHARACTER_VARYING_ARRAY = 'character varying[]',
}

export enum AllowedMimeTypes {
    TEXT_CSV = 'text/csv',
    TEXT_PLAIN = 'text/plain',
    APPLICATION_PDF = 'application/pdf',
    APPLICATION_ZIP = 'application/zip',
    IMAGE_PNG = 'image/png',
    IMAGE_JPEG = 'image/jpeg',
    APPLICATION_PGP = 'application/pgp-encrypted',
}

/**
 * Constants required to mantain multiple auth providers
 */
export enum AuthConstants {
    OUTREACH_AUTH_PROVIDER = 'outreach.auth.provider',
    OUTREACH_AUTH_USER = 'outreach.auth.user',
    OUTREACH_AUTH_TOKEN = 'outreach.auth.token',
    UAM_TENANT_ID = 'uamTenantId',
    CURED_TENANT_ID = 'curedTenantId',
    REDIRECT_URL = 'redirectUrl',
    ACCESS_TOKEN = 'accessToken',
    SESSION_EXPIRED = 'session_expired',
    INNOVACCER_DATASHOP_PROVIDER = 'innovaccer:uam',
    AUTH0_PROVIDER = 'innovaccer:auth0',
    USER = 'user',
    INNOVACCER_TENANT_HEADER = 'Innovaccer-Tenant',
}

/**
 * Access Centre permission keys (GET /me, roles, etc.). Value is backend key.
 * @see access-centre-api.md
 */
export enum AccessCentrePermissionKey {
    AccessManagementView = 'access_management.view',
    AccessManagementManage = 'access_management.manage',
    ContentView = 'content.view',
    ContentManage = 'content.manage',
    ContentPublish = 'content.publish',
    ContactsView = 'contacts.view',
    ContactsViewPhi = 'contacts.view_phi',
    ContactsUpload = 'contacts.upload',
    AudiencesView = 'audiences.view',
    AudiencesManage = 'audiences.manage',
    AudiencesViewContacts = 'audiences.view_contacts',
    AudiencesDownloadContacts = 'audiences.download_contacts',
    ValueView = 'value.view',
    ValueCreate = 'value.create',
    CurationsView = 'curations.view',
    CurationsManageDrafts = 'curations.manage_drafts',
    CurationsPublish = 'curations.publish',
    CurationsAbTesting = 'curations.ab_testing',
    CurationsAuditLog = 'curations.audit_log',
    InsightsViewDashboards = 'insights.view_dashboards',
    InsightsDownloadCsvs = 'insights.download_csvs',
    /** AI / Brand Voice Registration (CSV: Brand Voice Registration) */
    AiBrandRegistration = 'ai.brand_registration',
}

/**
 * How Access Centre route guards combine LaunchDarkly `accessCenter` with AccessCentrePermissionKey
 */
export enum AccessCentreRouteAccessMode {
    /** Route only when the flag is on and the permission key is granted. */
    RequireFeatureAndPermission = 'requireFeatureAndPermission',
    /** When the flag is not on, allow (legacy); when on, require the permission key. */
    AllowLegacyUnlessGated = 'allowLegacyUnlessGated',
}
